import React, { useEffect, useState } from 'react';
import { ArrowUpRight, Check, Cpu, TrendingUp, Zap } from 'lucide-react';
import styled from '../../styled';
import {
  AnimatedNumber,
  usePrefersReducedMotion
} from '../motion';

const Panel = styled('div')`
  position: relative;
  width: 100%;
  max-width: 560px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 16px 48px rgba(14, 28, 43, 0.1);
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
`;

const PanelHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
`;

const MonoLabel = styled('span')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #1890ff;
`;

const Dots = styled('div')`
  display: flex;
  gap: 6px;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #e2e8f0;
  }
`;

const fadeInCard = `
  @keyframes montageCardIn {
    from {
      opacity: 0;
      transform: translateY(14px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }
`;

const Card = styled('div')<{ visible: boolean; reduced: boolean }>`
  ${fadeInCard}

  position: relative;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  opacity: ${(p) => (p.visible ? 1 : 0)};
  animation: ${(p) =>
    p.visible && !p.reduced ? 'montageCardIn 0.6s ease both' : 'none'};
`;

const ChatBubbleUser = styled('div')`
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 12px 12px 4px 12px;
  color: #0e1c2b;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.5;
  align-self: flex-end;
  max-width: 85%;
  min-height: 45px;
  position: relative;
`;

const AgentReply = styled('div')<{ visible: boolean; reduced: boolean }>`
  ${fadeInCard}

  display: flex;
  gap: 10px;
  align-items: flex-start;
  color: #4f4f4f;
  font-size: 14px;
  line-height: 1.5;
  max-width: 92%;
  opacity: ${(p) => (p.visible ? 1 : 0)};
  animation: ${(p) =>
    p.visible && !p.reduced ? 'montageCardIn 0.5s ease both' : 'none'};
`;

const AgentAvatar = styled('div')`
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background-color: #1890ff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CardTitleRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const CardTitle = styled('div')`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
`;

const DraftPill = styled('span')`
  font-size: 11px;
  font-weight: 700;
  color: #1890ff;
  background-color: #e6f7ff;
  border-radius: 100px;
  padding: 3px 10px;
`;

const DraftName = styled('div')`
  font-size: 15px;
  font-weight: 700;
  color: #0e1c2b;
  margin-bottom: 6px;
`;

const DraftMeta = styled('div')`
  font-size: 13px;
  color: #4f4f4f;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
`;

const ReviewLink = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #1890ff;
`;

const StatsRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 460px) {
    grid-template-columns: 1fr;
  }
`;

const BigNumber = styled('div')`
  font-size: 24px;
  font-weight: 800;
  color: #0e1c2b;
  line-height: 1.1;
  margin-bottom: 4px;
`;

const StatCaption = styled('div')`
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
`;

const SwapRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #4f4f4f;
  padding: 6px 0;
  border-top: 1px solid #f1f5f9;

  strong {
    color: #0e1c2b;
  }
`;

const SwapValue = styled('span')`
  font-weight: 700;
  color: #237804;
  background-color: #f0fdf4;
  border-radius: 4px;
  padding: 2px 6px;
`;

const FunnelBarTrack = styled('div')`
  height: 6px;
  background-color: #f1f5f9;
  border-radius: 100px;
  overflow: hidden;
  margin-top: 4px;
`;

const FunnelBarFill = styled('div')<{ pct: number; reduced: boolean }>`
  height: 100%;
  width: ${(p) => p.pct}%;
  border-radius: 100px;
  background: linear-gradient(90deg, #1890ff, #69c0ff);
  transition: ${(p) => (p.reduced ? 'none' : 'width 1.2s ease')};
`;

const FunnelRow = styled('div')`
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FunnelLabelRow = styled('div')`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #4f4f4f;

  strong {
    color: #0e1c2b;
  }
`;

const USER_PROMPT = 'Draft a March sponsorship campaign for Bose';

export const AgenticMontage = () => {
  const reduced = usePrefersReducedMotion();
  // 0 = user prompt, 1 = agent reply, 2 = draft card, 3 = stat cards
  const [step, setStep] = useState(reduced ? 3 : 0);

  useEffect(() => {
    if (reduced) {
      setStep(3);
      return;
    }
    const timers = [
      window.setTimeout(() => setStep((s) => Math.max(s, 1)), 400),
      window.setTimeout(() => setStep((s) => Math.max(s, 2)), 1150),
      window.setTimeout(() => setStep((s) => Math.max(s, 3)), 1850)
    ];
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [reduced]);

  return (
    <Panel aria-label="Product preview: Affilimate Agent drafting a campaign">
      <PanelHeader>
        <MonoLabel>agent</MonoLabel>
        <Dots>
          <span />
          <span />
          <span />
        </Dots>
      </PanelHeader>

      <ChatBubbleUser>{USER_PROMPT}</ChatBubbleUser>

      <AgentReply visible={step >= 1} reduced={reduced}>
        <AgentAvatar>
          <Cpu size={14} />
        </AgentAvatar>
        <div>
          Done. I scoped 12 pages already earning on premium audio and put a
          draft in Campaigns for your review.
        </div>
      </AgentReply>

      <Card visible={step >= 2} reduced={reduced}>
        <CardTitleRow>
          <CardTitle>Campaign draft</CardTitle>
          <DraftPill>Ready for review</DraftPill>
        </CardTitleRow>
        <DraftName>Bose · March sponsored content</DraftName>
        <DraftMeta>
          <span>
            <Check size={12} /> 12 pages scoped
          </span>
          <span>Flat spend + CPA</span>
          <span>Auto-reverts Apr 1</span>
        </DraftMeta>
        <ReviewLink>
          Review draft <ArrowUpRight size={14} />
        </ReviewLink>
      </Card>

      <StatsRow>
        <Card visible={step >= 3} reduced={reduced}>
          <CardTitleRow>
            <CardTitle>Yield Optimizer</CardTitle>
            <Zap size={14} color="#1890ff" />
          </CardTitleRow>
          <BigNumber>
            +$
            <AnimatedNumber
              value={77.9}
              start={step >= 3}
              format={(n) => n.toFixed(1)}
            />
            K
          </BigNumber>
          <StatCaption>Monthly opportunity volume</StatCaption>
          <div style={{ marginTop: 10 }}>
            <SwapRow>
              <span>
                23K clicks → <strong>Wayfair</strong>
              </span>
              <SwapValue>+$14.5K</SwapValue>
            </SwapRow>
            <SwapRow>
              <span>
                17K clicks → <strong>Samsung</strong>
              </span>
              <SwapValue>+$8.2K</SwapValue>
            </SwapRow>
          </div>
        </Card>

        <Card visible={step >= 3} reduced={reduced}>
          <CardTitleRow>
            <CardTitle>AI discovery</CardTitle>
            <TrendingUp size={14} color="#1890ff" />
          </CardTitleRow>
          <FunnelRow>
            <FunnelLabelRow>
              <span>AI crawls</span>
              <strong>
                <AnimatedNumber value={4820} start={step >= 3} />
              </strong>
            </FunnelLabelRow>
            <FunnelBarTrack>
              <FunnelBarFill pct={step >= 3 ? 92 : 0} reduced={reduced} />
            </FunnelBarTrack>
          </FunnelRow>
          <FunnelRow>
            <FunnelLabelRow>
              <span>AI citations</span>
              <strong>
                <AnimatedNumber value={1140} start={step >= 3} />
              </strong>
            </FunnelLabelRow>
            <FunnelBarTrack>
              <FunnelBarFill pct={step >= 3 ? 56 : 0} reduced={reduced} />
            </FunnelBarTrack>
          </FunnelRow>
          <FunnelRow>
            <FunnelLabelRow>
              <span>Referrals</span>
              <strong>
                <AnimatedNumber value={312} start={step >= 3} />
              </strong>
            </FunnelLabelRow>
            <FunnelBarTrack>
              <FunnelBarFill pct={step >= 3 ? 24 : 0} reduced={reduced} />
            </FunnelBarTrack>
          </FunnelRow>
        </Card>
      </StatsRow>
    </Panel>
  );
};
